import { MyActionPlanContext, MyActionPlanInitialValues } from "./context";
import { useActionsContext } from "src/context/Actions/useActionsContext";
import { useGetOnePost } from "src/api/client-api/post/useGetOnePost";
import { computeAreQuestionsAnswered } from "./utils/areQuestionsAnswered";
import { useEffect, useState } from "react";
import { isActionEmpty, isActionMultilevel } from "src/utils/actionUtils";
import { useGetFieldValues, useGetOneActionPlanAction } from "src/api/client-api/ActionPlan/ActionPlanAction";
import { getImpactData } from "./utils/impactData/getImpactData";
import { useEntityScores } from "src/context/Scores/useEntityScores";
import { ActionOverviewSkeleton } from "src/components/Actions/Common/ActionOverviewCard";

type Props = {
    children: ReactNode;
    actionPlanActionId: string | undefined;
};
export const MyActionPlanProvider = (props: Props) => {
    const { lastSelectedVariation } = useEntityScores();
    const { olapScoresQuery, actionPlan, selectedActionPlan } = useActionsContext();
    const { actionPlanActionId } = props;

    const [isEditMode, _setIsEditMode] = useState<boolean>(false);
    const setIsEditMode = (v: boolean) => {
        _setIsEditMode(v);
    };

    const startingPostId = selectedActionPlan?.metadata.startingPost.id;
    const { data: startingPost } = useGetOnePost({ postId: startingPostId });

    const { data: actionPlanAction, isReady: isActionPlanActionReady } = useGetOneActionPlanAction({
        actionPlanID: actionPlan?.id,
        scoresQuery: olapScoresQuery,
        actionPlanActionId,
    });

    const { data: fieldValues, isReady: isFieldValueReady } = useGetFieldValues({ actionPlanActionId });

    const startingPostElements = startingPost?.elements;

    const areQuestionsAnswered = computeAreQuestionsAnswered({
        actionPlanAction: actionPlanAction,
        elements: startingPostElements,
    });

    const canBeGeneralized = isActionMultilevel(actionPlanAction?.action);

    // Maybe the useEffect should be kept as before in the Generalization block.
    // This would make it only computed in this block, since this is about this block only
    useEffect(() => {
        const canEdit = startingPostElements !== undefined;
        const shouldEdit = areQuestionsAnswered === false;

        if (canBeGeneralized === false) {
            setIsEditMode(false);
            return;
        }

        setIsEditMode(canEdit && shouldEdit);
    }, [startingPostElements, actionPlanAction, areQuestionsAnswered]);

    if (isActionPlanActionReady === false) {
        return <ActionOverviewSkeleton variant="engaged" />;
    }

    if (actionPlanAction === undefined) {
        // Error state
        return null;
    }

    const isEmpty = isActionEmpty(actionPlanAction.action);
    const impactDataSummary = getImpactData({
        actionPlanAction: actionPlanAction,
        scoreId: lastSelectedVariation.scoreId,
    });

    const items: MyActionPlanInitialValues = {
        actionPlanAction,
        impactDataSummary,
        isActionPlanActionReady,
        areQuestionsAnswered,
        isEditMode,
        setIsEditMode,
        startingPost,
        canBeGeneralized,
        isEmpty,
        fieldValues,
        isFieldValueReady,
    };
    return <MyActionPlanContext.Provider value={items}>{props.children}</MyActionPlanContext.Provider>;
};
